import { ReactComponent as FilterClearIcon } from "assets/icons/filter-clear.svg";
import { useGetProductionTypesAsItems, useGetProfessionalAreasAsItems } from "modules/codebooks";
import {
    FilterGroup,
    GenericSwitchFilter,
    SubscriptionMenu,
    recordsModuleSubscriptionCategories,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    useUpdateFilterPreferences,
} from "modules/common";
import { handleKeyDown } from "modules/common/utils/keyboardUtils";
import React, { useEffect, useRef, useState } from "react";
import { DeadlineFilter } from "./DeadlineFilter";

interface Props {
    professionalAreaFilter: string[];
    setProfessionalAreaFilter: React.Dispatch<React.SetStateAction<string[]>>;
    productionTypeFilter: string[];
    setProductionTypeFilter: React.Dispatch<React.SetStateAction<string[]>>;
    deadlineFilter: number[];
    setDeadlineFilter: React.Dispatch<React.SetStateAction<number[]>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export const RecordsFilters: React.FC<Props> = ({
    professionalAreaFilter,
    setProfessionalAreaFilter,
    productionTypeFilter,
    setProductionTypeFilter,
    deadlineFilter,
    setDeadlineFilter,
    setCurrentPage,
}) => {
    const PROFESSIONAL_AREA_FILTER_NAME = `${RecordsFilters.name}-professionalAreaFilter`;
    const PRODUCTION_TYPE_FILTER_NAME = `${RecordsFilters.name}-productionTypeFilter`;
    const DEADLINE_FILTER_NAME = `${RecordsFilters.name}-deadlineFilter`;

    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        PROFESSIONAL_AREA_FILTER_NAME,
        PRODUCTION_TYPE_FILTER_NAME,
        DEADLINE_FILTER_NAME,
    ]);

    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    const [showProfessionalAreaFilter, setShowProfessionalAreaFilter] = useState(false);
    const [showProductionTypeFilter, setShowProductionTypeFilter] = useState(false);
    const [showDeadlineFilter, setShowDeadlineFilter] = useState(false);

    const professionalAreaFilterRef = useRef<HTMLDivElement>(null);
    const productionTypeFilterRef = useRef<HTMLDivElement>(null);
    const deadlineFilterRef = useRef<HTMLDivElement>(null);

    const isProfessionalAreaFilterActive = professionalAreaFilter.length > 0;
    const isProductionTypeFilterActive = productionTypeFilter.length > 0;
    const isDeadlineFilterActive = deadlineFilter.length > 0;

    const handleProfessionalAreaFilterOutsideClick = () => setShowProfessionalAreaFilter(false);
    const handleProductionTypeFilterOutsideClick = () => setShowProductionTypeFilter(false);
    const handleDeadlineFilterOutsideClick = () => setShowDeadlineFilter(false);

    useOutsideClick(professionalAreaFilterRef, handleProfessionalAreaFilterOutsideClick);
    useOutsideClick(productionTypeFilterRef, handleProductionTypeFilterOutsideClick);
    useOutsideClick(deadlineFilterRef, handleDeadlineFilterOutsideClick);

    const handleProfessionalAreaFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setProfessionalAreaFilter(prev => {
            const newValues = prev.includes(value)
                ? prev.filter(s => s !== value)
                : [...prev, value];
            handleUpdateFilterPreferences(PROFESSIONAL_AREA_FILTER_NAME, value);

            return newValues;
        });
    };

    const handleProductionTypeFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setProductionTypeFilter(prev => {
            const newValues = prev.includes(value)
                ? prev.filter(s => s !== value)
                : [...prev, value];
            handleUpdateFilterPreferences(PRODUCTION_TYPE_FILTER_NAME, value);

            return newValues;
        });
    };

    const handleDeadlineFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const deadlineValue = Number(value);

        setDeadlineFilter(prev => {
            const newValues = prev.includes(deadlineValue)
                ? prev.filter(s => s !== deadlineValue)
                : [...prev, deadlineValue];

            handleUpdateFilterPreferences(DEADLINE_FILTER_NAME, deadlineValue);
            return newValues;
        });
    };

    const handleProfessionalAreaFilterClear = () => {
        updateFilterPreferencesMutation.mutate({
            filterName: PROFESSIONAL_AREA_FILTER_NAME,
            clearFilter: true,
        });
        setProfessionalAreaFilter([]);
    };

    const handleProductionTypeFilterClear = () => {
        updateFilterPreferencesMutation.mutate({
            filterName: PRODUCTION_TYPE_FILTER_NAME,
            clearFilter: true,
        });
        setProductionTypeFilter([]);
    };

    const handleDeadlineFilterClear = () => {
        updateFilterPreferencesMutation.mutate({
            filterName: DEADLINE_FILTER_NAME,
            clearFilter: true,
        });
        setDeadlineFilter([]);
    };

    const handleProfessionalAreaFilterClick = () => setShowProfessionalAreaFilter(prev => !prev);
    const handleProductionTypeFilterClick = () => setShowProductionTypeFilter(prev => !prev);
    const handleDeadlineFilterClick = () => setShowDeadlineFilter(prev => !prev);

    const handleUpdateFilterPreferences = (filterName: string, filterValue: number | string) => {
        if (typeof filterValue === "string") {
            updateFilterPreferencesMutation.mutate({
                filterName,
                filterStringValue: filterValue,
            });
        } else {
            updateFilterPreferencesMutation.mutate({
                filterName,
                filterIntValue: filterValue,
            });
        }
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        handleProfessionalAreaFilterClear();
        handleProductionTypeFilterClear();
        handleDeadlineFilterClear();
    };

    const professionalAreasAsItems = useGetProfessionalAreasAsItems();
    const productionTypesAsItems = useGetProductionTypesAsItems();

    useEffect(() => {
        const isLoading = filterPreferencesQuery.some(query => query.isLoading);
        if (isLoading) return;

        const professionalAreaPreference = filterPreferencesQuery.find(
            query => query.data?.filterName === PROFESSIONAL_AREA_FILTER_NAME
        );

        const productionTypePreference = filterPreferencesQuery.find(
            query => query.data?.filterName === PRODUCTION_TYPE_FILTER_NAME
        );

        const deadlinePreference = filterPreferencesQuery.find(
            query => query.data?.filterName === DEADLINE_FILTER_NAME
        );

        const professionalAreaValues = professionalAreaPreference?.data?.filterStringValues;
        if (professionalAreaValues?.length) {
            setProfessionalAreaFilter(professionalAreaValues);
        }

        const productionTypeValues = productionTypePreference?.data?.filterStringValues;
        if (productionTypeValues?.length) {
            setProductionTypeFilter(productionTypeValues);
        }

        const deadlineValues = deadlinePreference?.data?.filterIntValues;
        if (deadlineValues?.length) {
            setDeadlineFilter(deadlineValues);
        }
    }, [filterPreferencesQuery.some(q => q.isLoading)]);

    return (
        <FilterGroup>
            <div className="mr-2 records-filters" ref={professionalAreaFilterRef}>
                <GenericSwitchFilter
                    name="STRUČNO PODRUČJE"
                    onChange={handleProfessionalAreaFilterChange}
                    onClear={handleProfessionalAreaFilterClear}
                    onClick={handleProfessionalAreaFilterClick}
                    values={professionalAreaFilter}
                    isActive={isProfessionalAreaFilterActive}
                    showDropdown={showProfessionalAreaFilter}
                    list={professionalAreasAsItems.map(item => ({
                        key: item.guid,
                        value: item.label,
                    }))}
                />
            </div>

            <div className="mr-2 records-filters" ref={productionTypeFilterRef}>
                <GenericSwitchFilter
                    name="VRSTA PROIZVODNJE"
                    onChange={handleProductionTypeFilterChange}
                    onClear={handleProductionTypeFilterClear}
                    onClick={handleProductionTypeFilterClick}
                    values={productionTypeFilter}
                    isActive={isProductionTypeFilterActive}
                    showDropdown={showProductionTypeFilter}
                    list={productionTypesAsItems.map(item => ({
                        key: item.guid,
                        value: item.label,
                    }))}
                />
            </div>

            <div className="mr-2 records-filters" ref={deadlineFilterRef}>
                <DeadlineFilter
                    name="ROK ISPUNJAVANJA"
                    onChange={handleDeadlineFilterChange}
                    onClear={handleDeadlineFilterClear}
                    onClick={handleDeadlineFilterClick}
                    isActive={isDeadlineFilterActive}
                    showDropdown={showDeadlineFilter}
                    values={deadlineFilter}
                />
            </div>
            <div className="mr-2 records-filters">
                <SubscriptionMenu subscriptionCategories={recordsModuleSubscriptionCategories} />
            </div>

            {(professionalAreaFilter.length > 0 ||
                productionTypeFilter.length > 0 ||
                deadlineFilter.length > 0) && (
                <FilterClearIcon
                    className="filter-group__icon clear-filter"
                    onClick={handleClearFilter}
                    tabIndex={0}
                    onKeyDown={e => handleKeyDown(e, handleClearFilter)}
                />
            )}
        </FilterGroup>
    );
};
